import * as React from 'react';
import { ReactNode, CSSProperties } from 'react';
import { useMemo, forwardRef } from 'react';
import {
	Typography,
	VirtualRepeater,
	VirtualRepeaterComponent,
	ListPlaceholder,
	Card,
	Button,
	Icon,
	Badge,
} from '@seeneco-ui/core';
import { prettyDate } from '@seeneco-ui/core/utils/date';

import withSoftFetching from '@core/hocs/with-soft-fetching.hoc';
import { LeadRegistrationStatus, LeadRegistrationStatusName } from '@leads/model';
import {
	Root,
	HeaderLayout,
	ControlsLayout,
} from './styled';


const CompaniesList: VirtualRepeaterComponent<Lead> = VirtualRepeater;

type LeadsVirtualListProps = {
	isFetching: boolean;
	items: Array<Lead>;
	onRequestUpdateItem: (lead: Lead) => void;
	onSendEmailNotification: (lead: Lead) => void;
};

const LeadsVirtualList: React.SFC<LeadsVirtualListProps> = forwardRef((props, ref) => {
	const {
		items,
		isFetching,
		onRequestUpdateItem,
		onSendEmailNotification,
	} = props;

	const renderItem = useMemo(() => ({ item, isEven, isActive, toggleActive }) => {
		const lead = item as Lead;
		const statusCode = lead?.Status?.Code || LeadRegistrationStatus.UNKNOWN;
		const isRegistered = statusCode === LeadRegistrationStatus.REGISTERED;
		const isEmailSent = statusCode === LeadRegistrationStatus.EMAIL_SENT;
		const handleUpdateItem = () => onRequestUpdateItem(item);
		const handleSendEmail = () => onSendEmailNotification(item);

		return (
			<Card
				appearance='repeater-item'
				minHeight={40}
				fullWidth
				bg={isEven ? 'stealthy' : 'white'}
				style={{
					borderRight: 0,
					borderLeft: 0,
					padding: '12px 32px',
				}}
				onClick={toggleActive}>
				<ItemContent
					isActive={isActive}
					content={[
						prettyDate(lead.CreationDate),
						lead.CompanyName || '-',
						lead.CompanyTaxCode || '-',
						lead.ContactName || '-',
						lead.ContactPhone || '-',
						lead.ContactEmail || '-',
						lead.Comment || '-',
						<Badge color={StatusBadgeColor[statusCode]}>
							{LeadRegistrationStatusName[statusCode]}
						</Badge>,
					]}
				/>
				{
					isActive &&
					<ControlsLayout>
						<Button
							appearance='text'
							size='small'
							stopPropagation
							onClick={handleUpdateItem}>
							<Icon type='edit' color='primary' width={16} height={16} />
							<Typography.Nbsp />
							Изменить
						</Button>
						{
							!isRegistered &&
							<Button
								appearance='text'
								size='small'
								stopPropagation
								onClick={handleSendEmail}>
								<Icon type='send' color='primary' width={16} height={16} />
								<Typography.Nbsp />
								{isEmailSent ? 'Отправить e-mail повторно' : 'Отправить e-mail'}
							</Button>
						}
					</ControlsLayout>
				}
			</Card>
		);
	}, []);

	const renderListPlaceholder = useMemo(() => () => {
		return (
			<ListPlaceholder.Layout>
				<Typography.Text>
					В системе не найдено ни одного лида, удовлетворяющего критериям отбора
				</Typography.Text>
			</ListPlaceholder.Layout>
		);
	}, []);

	return (
		<Root ref={ref as any}>
			<HeaderLayout>
				<ItemContent
					content={[
						<Typography.Label fontSize={12}>Дата</Typography.Label>,
						<Typography.Label fontSize={12}>Компания</Typography.Label>,
						<Typography.Label fontSize={12}>ИНН</Typography.Label>,
						<Typography.Label fontSize={12}>Контактное лицо</Typography.Label>,
						<Typography.Label fontSize={12}>Номер телефона</Typography.Label>,
						<Typography.Label fontSize={12}>E-mail</Typography.Label>,
						<Typography.Label fontSize={12}>Комментарий</Typography.Label>,
						<Typography.Label fontSize={12}>Статус</Typography.Label>,
					]}
				/>
			</HeaderLayout>
			<CompaniesList
				items={items}
				isFetching={isFetching}
				renderItem={renderItem}
				placeholder={renderListPlaceholder()}
			/>
		</Root>
	);
});

type ItemContentProps = {
	isActive?: boolean;
	content: [
		ReactNode,
		ReactNode,
		ReactNode,
		ReactNode,
		ReactNode,
		ReactNode,
		ReactNode,
		ReactNode,
	];
};

const ellipsis = {
	whiteSpace: 'nowrap',
	maxWidth: '100%',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
};

const styles = {
	root: (isActive: boolean) => {
		return {
			minHeight: 40,
			display: 'flex',
			alignItems: isActive ? 'flex-start' : 'center',
		} as CSSProperties;
	},
	date: {
		width: '100px',
	} as CSSProperties,
	company: (isActive: boolean) => {
		return {
			width: '20%',
			wordBreak: 'break-all',
			marginLeft: 10,
			...(!isActive ? ellipsis : {})
		} as CSSProperties;
	},
	taxCode: {
		width: '100px',
		marginLeft: 10,
	} as CSSProperties,
	contactName: (isActive: boolean) => {
		return {
			width: '15%',
			marginLeft: 10,
			wordBreak: 'break-all',
			...(!isActive ? ellipsis : {})
		} as CSSProperties;
	},
	phoneNumber: (isActive: boolean) => {
		return {
			width: '15%',
			marginLeft: 10,
			...(!isActive ? ellipsis : {})
		} as CSSProperties;
	},
	email: (isActive: boolean) => {
		return {
			width: '15%',
			marginLeft: 10,
			...(!isActive ? ellipsis : {})
		} as CSSProperties;
	},
	comment: (isActive: boolean) => {
		return {
			width: '20%',
			marginLeft: 10,
			...(!isActive ? ellipsis : {})
		} as CSSProperties;
	},
	status: {
		width: '160px',
		textAlign: 'right',
		marginLeft: 10,
	} as CSSProperties,
};

const ItemContent: React.SFC<ItemContentProps> = ({ isActive, content }) => {
	return (
		<div style={styles.root(isActive)}>
			<div style={styles.date}>
				{content[0]}
			</div>
			<div style={styles.company(isActive)}>
				{content[1]}
			</div>
			<div style={styles.taxCode}>
				{content[2]}
			</div>
			<div style={styles.contactName(isActive)}>
				{content[3]}
			</div>
			<div style={styles.phoneNumber(isActive)}>
				{content[4]}
			</div>
			<div style={styles.email(isActive)}>
				{content[5]}
			</div>
			<div style={styles.comment(isActive)}>
				{content[6]}
			</div>
			<div style={styles.status}>
				{content[7]}
			</div>
		</div>
	);
}

LeadsVirtualList.displayName = 'LeadsVirtualList';
LeadsVirtualList.defaultProps = {};

const StatusBadgeColor = {
	UNKNOWN: 'gray',
	EMAIL_SENT: 'orange',
	REGISTERED: 'green',
};

export default withSoftFetching<LeadsVirtualListProps>({
	enableCover: true,
	shouldUpdateWhen: (props, nextProps) => nextProps.items.length === 0,
})(LeadsVirtualList);
